/**
 * UI Bridge Server Types
 *
 * Shared types for server adapters.
 */

import type { UIBridgeConfig, ElementHistoryOptions } from '@qontinui/ui-bridge/core';
import type { UiBridgeErrorCode } from '@qontinui/ui-bridge/diagnostics';
import type {
  ControlActionRequest,
  ControlActionResponse,
  ComponentActionRequest,
  ComponentActionResponse,
  FindRequest,
  FindResponse,
  ControlSnapshot,
  WorkflowRunRequest,
  WorkflowRunResponse,
} from '@qontinui/ui-bridge/control';
import type { RenderLogEntry, RenderLogEntryType } from '@qontinui/ui-bridge/render-log';
import type {
  SearchCriteria,
  SearchResponse,
  NLActionRequest,
  NLActionResponse,
  AssertionRequest,
  AssertionResult,
  BatchAssertionRequest,
  BatchAssertionResult,
  SemanticSnapshot,
  SemanticDiff,
  SemanticSearchCriteria,
  SemanticSearchResponse,
  ScreenAnalysis,
} from '@qontinui/ui-bridge/ai';
import type { PageHealthReport } from './page-health';

/**
 * Server configuration
 */
export interface UIBridgeServerConfig extends UIBridgeConfig {
  /** Base path for API routes */
  basePath?: string;
  /** Enable CORS */
  cors?: boolean | CORSOptions;
  /** Authentication middleware */
  authenticate?: (req: unknown) => boolean | Promise<boolean>;
  /** Rate limiting */
  rateLimit?: RateLimitOptions;
}

/**
 * CORS options
 */
export interface CORSOptions {
  /** Allowed origins */
  origin?: string | string[] | boolean;
  /** Allowed methods */
  methods?: string[];
  /** Allowed headers */
  headers?: string[];
  /** Expose headers */
  exposeHeaders?: string[];
  /** Allow credentials */
  credentials?: boolean;
  /** Max age for preflight cache */
  maxAge?: number;
}

/**
 * Rate limit options
 */
export interface RateLimitOptions {
  /** Time window in milliseconds */
  windowMs?: number;
  /** Max requests per window */
  max?: number;
  /** Message when rate limited */
  message?: string;
}

/**
 * API response wrapper
 */
export interface APIResponse<T = unknown> {
  /** Whether the request succeeded */
  success: boolean;
  /** Response data */
  data?: T;
  /** Error message if failed (human-readable, dual-audience — plan goal #3) */
  error?: string;
  /**
   * Stable machine-readable diagnostic code. Populated on every
   * `success: false` response (plan Phase 1 — required on failure;
   * optional at the type level only because successful responses omit it).
   * Always a canonical `UiBridgeErrorCode` (mapped from internal/legacy
   * codes via `error-mapper.ts`).
   */
  code?: UiBridgeErrorCode;
  /** Request timestamp */
  timestamp: number;
  /** Response time in milliseconds (set by server) */
  durationMs?: number;
}

/**
 * Render log query parameters
 */
export interface RenderLogQuery {
  /** Filter by entry type */
  type?: RenderLogEntryType;
  /** Filter entries since timestamp */
  since?: number;
  /** Filter entries until timestamp */
  until?: number;
  /** Limit number of results */
  limit?: number;
}

/**
 * Server handler interface
 *
 * Implementations provide these handlers for different frameworks.
 */
export interface UIBridgeServerHandlers {
  // Render log endpoints
  getRenderLog: (query?: RenderLogQuery) => Promise<APIResponse<RenderLogEntry[]>>;
  clearRenderLog: () => Promise<APIResponse<void>>;
  captureSnapshot: () => Promise<APIResponse<unknown>>;
  getRenderLogPath: () => Promise<APIResponse<{ path: string }>>;

  // Control endpoints
  getElements: () => Promise<APIResponse<ControlSnapshot['elements']>>;
  getElement: (id: string) => Promise<APIResponse<ControlSnapshot['elements'][0]>>;
  getElementState: (id: string) => Promise<APIResponse<unknown>>;
  executeElementAction: (
    id: string,
    request: ControlActionRequest
  ) => Promise<APIResponse<ControlActionResponse>>;

  // Component endpoints
  getComponents: () => Promise<APIResponse<ControlSnapshot['components']>>;
  getComponent: (id: string) => Promise<APIResponse<ControlSnapshot['components'][0]>>;
  getComponentState: (id: string) => Promise<
    APIResponse<{
      state: Record<string, unknown>;
      computed: Record<string, unknown>;
      timestamp: number;
    }>
  >;
  executeComponentAction: (
    id: string,
    request: ComponentActionRequest
  ) => Promise<APIResponse<ComponentActionResponse>>;

  // Find endpoints
  find: (request?: FindRequest) => Promise<APIResponse<FindResponse>>;
  /**
   * @deprecated Use find() instead
   */
  discover: (request?: FindRequest) => Promise<APIResponse<FindResponse>>;
  getControlSnapshot: () => Promise<APIResponse<ControlSnapshot>>;

  // Workflow endpoints
  getWorkflows: () => Promise<APIResponse<ControlSnapshot['workflows']>>;
  runWorkflow: (
    id: string,
    request?: WorkflowRunRequest
  ) => Promise<APIResponse<WorkflowRunResponse>>;
  getWorkflowStatus: (runId: string) => Promise<APIResponse<WorkflowRunResponse>>;

  // Debug endpoints
  getActionHistory: (limit?: number) => Promise<APIResponse<unknown[]>>;
  getMetrics: () => Promise<APIResponse<unknown>>;
  highlightElement: (id: string) => Promise<APIResponse<void>>;
  getElementTree: () => Promise<APIResponse<unknown>>;

  // AI-native endpoints
  aiSearch: (criteria: SearchCriteria) => Promise<APIResponse<SearchResponse>>;
  aiExecute: (request: NLActionRequest) => Promise<APIResponse<NLActionResponse>>;
  aiAssert: (request: AssertionRequest) => Promise<APIResponse<AssertionResult>>;
  aiAssertBatch: (request: BatchAssertionRequest) => Promise<APIResponse<BatchAssertionResult>>;
  getSemanticSnapshot: () => Promise<APIResponse<SemanticSnapshot>>;
  getSemanticDiff: (since?: number) => Promise<APIResponse<SemanticDiff | null>>;
  getPageSummary: () => Promise<APIResponse<string>>;

  // Screen analysis
  getScreenAnalysis: () => Promise<APIResponse<ScreenAnalysis>>;

  // Semantic search (embedding-based)
  aiSemanticSearch: (
    criteria: SemanticSearchCriteria
  ) => Promise<APIResponse<SemanticSearchResponse>>;

  // Element event log
  getElementHistory: (
    elementId: string,
    options?: ElementHistoryOptions
  ) => Promise<APIResponse<unknown[]>>;

  // Performance diagnostics
  getPerformanceEntries: () => Promise<APIResponse<unknown>>;
  clearPerformanceEntries: () => Promise<APIResponse<{ cleared: boolean }>>;
  getBrowserEvents: (params?: {
    type?: string;
    since?: number;
    limit?: number;
  }) => Promise<APIResponse<{ events: unknown[]; count: number }>>;

  // Page health diagnostics
  pageHealth?: () => Promise<APIResponse<PageHealthReport>>;

  // Enhanced discovery endpoints
  query: (request: {
    selector: string;
    limit?: number;
    includeState?: boolean;
  }) => Promise<APIResponse<{ elements: unknown[]; count: number }>>;

  waitForElement: (request: {
    selector?: string;
    elementId?: string;
    timeout?: number;
    pollInterval?: number;
  }) => Promise<APIResponse<{ found: boolean; element?: unknown; waitedMs: number }>>;

  // App-agnostic convenience endpoints
  clickByText: (request: {
    text: string;
    tag?: string;
    exact?: boolean;
  }) => Promise<APIResponse<{ clicked: boolean; element?: unknown }>>;

  clickBySelector: (request: {
    selector: string;
    index?: number;
  }) => Promise<APIResponse<{ clicked: boolean; element?: unknown }>>;

  typeInto: (request: {
    selector?: string;
    label?: string;
    text: string;
    clear?: boolean;
  }) => Promise<APIResponse<{ typed: boolean; element?: unknown }>>;

  readValue: (request: {
    selector: string;
    index?: number;
  }) => Promise<APIResponse<{ value: string | null; length: number }>>;

  findByText: (request: { text: string; tag?: string; exact?: boolean }) => Promise<
    APIResponse<
      Array<{
        index: number;
        tag: string;
        text: string;
        rect: { x: number; y: number; width: number; height: number };
        disabled: boolean;
        visible: boolean;
      }>
    >
  >;

  // Diagnostics endpoint (SDK runtime state — pre-existing)
  getDiagnostics: () => Promise<APIResponse<unknown>>;

  /**
   * Diagnostic catalog endpoints (plan Phase 2). Read-only, backed by the
   * single source `diagnostics/codes.json` (via the generated
   * `@qontinui/ui-bridge/diagnostics` catalog). `getDiagnosticsCatalog`
   * returns every code (agent bootstrap); `getDiagnosticCode` returns one
   * code's entry, 404 + canonical `UiBridgeErrorCode` on an unknown code.
   */
  getDiagnosticsCatalog: () => Promise<APIResponse<unknown>>;
  getDiagnosticCode: (code: string) => Promise<APIResponse<unknown>>;

  // Navigation adapter endpoints
  getRoutes: () => Promise<APIResponse<Array<{ name: string; path: string }>>>;
  navigateByAdapter: (request: {
    page: string;
  }) => Promise<APIResponse<{ navigated: boolean; route: { name: string; path: string } }>>;
}

/**
 * Route definition
 */
export interface RouteDefinition {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  handler: string; // Key in UIBridgeServerHandlers
  params?: string[]; // URL params to extract
  bodyRequired?: boolean;
}

/**
 * All UI Bridge routes
 */
export const UI_BRIDGE_ROUTES: RouteDefinition[] = [
  // Render log
  { method: 'GET', path: '/render-log', handler: 'getRenderLog' },
  { method: 'GET', path: '/control/render-log', handler: 'getRenderLog' }, // Alias under /control/
  { method: 'DELETE', path: '/render-log', handler: 'clearRenderLog' },
  { method: 'POST', path: '/render-log/snapshot', handler: 'captureSnapshot' },
  { method: 'GET', path: '/render-log/path', handler: 'getRenderLogPath' },

  // Control - Elements
  { method: 'GET', path: '/control/elements', handler: 'getElements' },
  { method: 'GET', path: '/control/element/:id', handler: 'getElement', params: ['id'] },
  { method: 'GET', path: '/control/element/:id/state', handler: 'getElementState', params: ['id'] },
  {
    method: 'POST',
    path: '/control/element/:id/action',
    handler: 'executeElementAction',
    params: ['id'],
    bodyRequired: true,
  },

  // Control - Components
  { method: 'GET', path: '/control/components', handler: 'getComponents' },
  { method: 'GET', path: '/control/component/:id', handler: 'getComponent', params: ['id'] },
  {
    method: 'GET',
    path: '/control/component/:id/state',
    handler: 'getComponentState',
    params: ['id'],
  },
  {
    method: 'POST',
    path: '/control/component/:id/action/:actionId',
    handler: 'executeComponentAction',
    params: ['id', 'actionId'],
    bodyRequired: true,
  },

  // Find (formerly Discovery)
  { method: 'POST', path: '/control/find', handler: 'find' },
  { method: 'POST', path: '/control/discover', handler: 'discover' }, // @deprecated Use /control/find
  { method: 'GET', path: '/control/snapshot', handler: 'getControlSnapshot' },
  {
    method: 'POST',
    path: '/control/viewport-constraints',
    handler: 'setViewportConstraints',
    bodyRequired: true,
  },

  // Workflows
  { method: 'GET', path: '/control/workflows', handler: 'getWorkflows' },
  { method: 'POST', path: '/control/workflow/:id/run', handler: 'runWorkflow', params: ['id'] },
  {
    method: 'GET',
    path: '/control/workflow/:runId/status',
    handler: 'getWorkflowStatus',
    params: ['runId'],
  },

  // Element event log
  {
    method: 'GET',
    path: '/debug/element-history/:id',
    handler: 'getElementHistory',
    params: ['id'],
  },

  // Debug
  { method: 'GET', path: '/debug/action-history', handler: 'getActionHistory' },
  { method: 'GET', path: '/debug/metrics', handler: 'getMetrics' },
  { method: 'POST', path: '/debug/highlight/:id', handler: 'highlightElement', params: ['id'] },
  { method: 'GET', path: '/debug/element-tree', handler: 'getElementTree' },

  // AI-native endpoints
  { method: 'POST', path: '/ai/search', handler: 'aiSearch', bodyRequired: true },
  { method: 'POST', path: '/ai/execute', handler: 'aiExecute', bodyRequired: true },
  { method: 'POST', path: '/ai/assert', handler: 'aiAssert', bodyRequired: true },
  { method: 'POST', path: '/ai/assert/batch', handler: 'aiAssertBatch', bodyRequired: true },
  { method: 'GET', path: '/ai/snapshot', handler: 'getSemanticSnapshot' },
  { method: 'GET', path: '/ai/diff', handler: 'getSemanticDiff' },
  { method: 'GET', path: '/ai/summary', handler: 'getPageSummary' },
  { method: 'GET', path: '/ai/analyze', handler: 'getScreenAnalysis' },
  { method: 'POST', path: '/ai/semantic-search', handler: 'aiSemanticSearch', bodyRequired: true },

  // Performance diagnostics
  { method: 'GET', path: '/control/performance-entries', handler: 'getPerformanceEntries' },
  {
    method: 'POST',
    path: '/control/performance-entries/clear',
    handler: 'clearPerformanceEntries',
  },
  { method: 'GET', path: '/control/browser-events', handler: 'getBrowserEvents' },

  // Page health diagnostics
  { method: 'POST', path: '/control/page-health', handler: 'pageHealth' },

  // Enhanced discovery & navigation
  { method: 'POST', path: '/control/query', handler: 'query', bodyRequired: true },
  {
    method: 'POST',
    path: '/control/waitForElement',
    handler: 'waitForElement',
    bodyRequired: true,
  },

  // App-agnostic convenience endpoints
  {
    method: 'POST',
    path: '/control/page/click-by-text',
    handler: 'clickByText',
    bodyRequired: true,
  },
  {
    method: 'POST',
    path: '/control/page/click-by-selector',
    handler: 'clickBySelector',
    bodyRequired: true,
  },
  { method: 'POST', path: '/control/page/type-into', handler: 'typeInto', bodyRequired: true },
  { method: 'POST', path: '/control/page/read-value', handler: 'readValue', bodyRequired: true },
  { method: 'POST', path: '/control/page/find-by-text', handler: 'findByText', bodyRequired: true },

  // Diagnostics — SDK runtime state (pre-existing)
  { method: 'GET', path: '/diagnostics', handler: 'getDiagnostics' },

  // Diagnostic catalog (plan Phase 2 — read-only, codes.json-backed).
  // `/diagnostics/catalog` is registered before `/diagnostics/:code` so the
  // literal segment is not captured as a `:code` param (matters for both the
  // express Router and the standalone first-match router).
  { method: 'GET', path: '/diagnostics/catalog', handler: 'getDiagnosticsCatalog' },
  {
    method: 'GET',
    path: '/diagnostics/:code',
    handler: 'getDiagnosticCode',
    params: ['code'],
  },

  // Navigation adapter
  { method: 'GET', path: '/control/page/routes', handler: 'getRoutes' },
  {
    method: 'POST',
    path: '/control/page/navigate-to',
    handler: 'navigateByAdapter',
    bodyRequired: true,
  },
];

/**
 * WebSocket message types
 */
export type WebSocketMessageType =
  | 'subscribe'
  | 'unsubscribe'
  | 'event'
  | 'snapshot'
  | 'action'
  | 'error';

/**
 * WebSocket message
 */
export interface WebSocketMessage<T = unknown> {
  type: WebSocketMessageType;
  channel?: string;
  data?: T;
  error?: string;
  timestamp: number;
}
